import React, { createRef, useCallback, useState } from "react";

import AnemoneIllustration from "images/illustrations/Anemone-Illustration-dark.inline.svg";
import MountainIllustration from "images/illustrations/Mountain_illustration.inline.svg";

import Participants from "../../components/shared/participants";
import { useTheme } from "../../context/ThemeContext";
import { useFilterExperiencesByLocationSlug } from "../../hooks/useFilterExperiencesByLocationSlug";
import { formatContent } from "../../utils/formatContent";
import { DesktopTabletOnly, MobileOnly } from "../base/wrapper/device";
import GeneralAccordionSetWrapper from "../shared/accordions/generalAccordionSetWrapper";
import TabsWrapper from "../shared/tabs/tabsWrapper";
import TabExperiences from "./project-tabs/tabExperiences";
import TabMeetTeam from "./project-tabs/tabMeetTeam";

const ProjectOverview = ({
  subtitle,
  tabsDetails,
  locationData,
  location,
  locationPageSlugs,
  direction,
  hideIllustration,
  sectionContainerClass,
  reviewTitle,
  reviewSubTitle,
  reviewBlocks
}) => {
  const { currentBrand, planetDomain, peopleDomain } = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const experiencesList = useFilterExperiencesByLocationSlug(locationPageSlugs);
  const allTeamMembers =
    locationData
      ?.flatMap((location) => location?.teamMembers?.nodes)
      ?.filter((removeBlank) => removeBlank) ?? [];

  const uniqueTeamMembers =
    allTeamMembers
      ?.filter((v, i, a) => a.indexOf(v) === i)
      ?.filter((removeBlank) => removeBlank) ?? [];

  const teamMembers =
    uniqueTeamMembers
      ?.map((teamMember) => {
        return {
          title: teamMember?.title,
          featuredImage: teamMember?.featuredImage?.node?.localFile?.extension
            ? teamMember?.featuredImage?.node?.localFile
            : null,
          sourceFallbackImage: teamMember?.featuredImage?.node?.sourceUrl,
          memberTitle: teamMember?.teamMembersFields?.teamPosition ?? "",
          content: teamMember?.content,
          uri: teamMember?.uri,
          memberVideo: teamMember?.teamMembersFields?.memberVideo ?? "",
          memberSnrStaff:
            teamMember?.teamMembersFields?.seniorStaffTeamMember ?? "",
          teamMember
        };
      })
      ?.filter((removeBlank) => removeBlank) ?? [];

  const meetTeamData = {
    title: "Meet the team",
    teamMembers
  };

  const experiencesData = {
    title: "Experiences",
    tabLabel: "GVI experiences",
    tabDesc:
      "<p>We want you to make the most of the chance to live in – and contribute towards –  the most diverse and unique wildernesses and communities on earth. Introducing GVI Experiences – immersive adventure, cultural and wellness activities exclusive to GVI that have been specially designed in collaboration with our local partners to support and stimulate sustainable economic development.</p> <p>Enhance your impact. Expand your adventure. Explore your world.</p>"
  };

  const sectionHead = subtitle ?? "";

  const tabs =
    tabsDetails &&
    tabsDetails
      ?.map((tab) => {
        const formattedContent = formatContent({
          content: tab?.tabContentDesc,
          currentBrand,
          planetDomain,
          peopleDomain
        });

        return {
          id: tab?.genTabTitle?.toLowerCase().replace(/ /g, "-"),
          ref: createRef(),
          title: tab?.genTabTitle,
          comp: tab?.genTabTitle?.includes("Meet the team") ? (
            <TabMeetTeam meetTeamData={meetTeamData} />
          ) : tab?.genTabTitle?.includes("Experiences") ? (
            <TabExperiences
              gviExperiencesLabel={experiencesData?.tabLabel}
              gviExperiencesDescription={experiencesData?.tabDesc}
              experiences={experiencesList}
            />
          ) : (
            <div
              className="tab-content_html"
              dangerouslySetInnerHTML={{
                __html: formattedContent
              }}
            />
          )
        };
      })
      ?.filter((tab) => tab.id);

  const handleNextSlide = useCallback(() => {
    if (currentSlide === tabs.length - 1) setCurrentSlide(0);
    else setCurrentSlide((c) => c + 1);
  }, [tabs, currentSlide]);

  const handleTabSlide = useCallback(
    (activeTab) => {
      const activeTabIndex = tabs.findIndex((tab) => tab.id === activeTab);
      if (currentSlide !== activeTabIndex) {
        setCurrentSlide(activeTabIndex);
      }
    },
    [currentSlide, tabs]
  );

  const updateNavigator = (idx) => {
    setCurrentSlide(idx);
  };

  return (
    <>
      {tabs && tabs?.length > 0 && (
        <section className={sectionContainerClass}>
          {!hideIllustration && (
            <>
              <MountainIllustration className="absolute hidden md:block planet:hidden md:w-[50vw] -right-[14vw] bottom-[10vw] xl:w-2/5" />
              <AnemoneIllustration className="absolute -bottom-[25vw] -right-[25vw] hidden md:planet:block opacity-15" />
            </>
          )}

          <div className="container w-[90vw] py-2 sm:pb-4 sm:pt-8 md:w-4/5">
            <h1 className="pb-8 font-light">{sectionHead}</h1>

            <div
              className={`flex flex-col w-full md:sticky-left-section md:pr-10 ${
                direction === "vertical" ? "md:flex-row" : "md:flex-col"
              }`}
            >
              <MobileOnly>
                <>
                  <GeneralAccordionSetWrapper
                    list={tabs}
                    currentSlide={currentSlide}
                    setCurrentSlide={handleNextSlide}
                    accordionIdentifier="id"
                    contentClassName={`bg-white planet:gvi-dark-gray-bg tab-content tab-space planet:gvi-light-gray-text text-generic-text p-3`}
                  />
                </>
              </MobileOnly>
              <DesktopTabletOnly>
                <TabsWrapper
                  defaultSelectedTab={tabs[currentSlide]?.id}
                  tabs={tabs}
                  standalone={false}
                  isVertical={true}
                  updateSelectedTab={handleTabSlide}
                  additionalContentClass={
                    "bg-white planet:gvi-dark-gray-bg tab-content tab-space planet:gvi-light-gray-text text-generic-text"
                  }
                />
              </DesktopTabletOnly>
            </div>

            <div className="relative hidden sm:block">
              {tabs && tabs.length > 0 && (
                <button
                  onClick={handleNextSlide}
                  className="text-base leading-[19px] planet:text-white text-black-lighter underline"
                >
                  {currentSlide === tabs.length - 1 ? (
                    <span>Read about {tabs[0]?.title} next</span>
                  ) : (
                    <span>Read about {tabs[currentSlide + 1]?.title} next</span>
                  )}
                </button>
              )}
            </div>
            {reviewTitle && reviewSubTitle && reviewBlocks && (
              <Participants
                key={"participants"}
                reviewTitle={reviewTitle}
                reviewSubTitle={reviewSubTitle}
                reviewBlocks={reviewBlocks}
              />
            )}
          </div>
        </section>
      )}
    </>
  );
};

export default ProjectOverview;
ProjectOverview.defaultProps = {
  showParticipants: false
};

ProjectOverview.defaultProps = {
  direction: "vertical",
  hideIllustration: false,
  sectionContainerClass:
    "relative flex items-center justify-center px-4 pb-12 md:px-0 overflow-hidden"
};
